import { z } from "zod";
import { canonicalize, } from "../protocol/canonicalize.js";
import { createEnforcementEffect, enforcementDecisionSchema, enforcementEffectSchema, enforcementPolicySchema, railActionIntentSchema, railActionKindSchema, } from "../protocol/enforcement.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, verificationStatusSchema, } from "../protocol/identifiers.js";
import { machineReadableVerdictReceiptSchema, verifyVerdictReceipt, } from "../protocol/verdict.js";
import { resolveStoredVerdict } from "../verdict-law.js";
export const railAdapterKindSchema = z.enum([
    "github",
    "ci",
    "jira",
    "linear",
    "erp",
    "marketplace",
    "stripe",
    "x402",
    "ap2",
]);
export const adapterCapabilityModeSchema = z.enum([
    "fully_mediated",
    "cooperative",
    "passive",
]);
export const railAdapterProfileSchema = z
    .object({
    kind: railAdapterKindSchema,
    mode: adapterCapabilityModeSchema,
    version: z.string().trim().min(1),
    supportedActions: z.array(railActionKindSchema),
    enforcementGuaranteed: z.boolean(),
    takesCustody: z.literal(false),
})
    .strict()
    .superRefine((value, context) => {
    if (value.mode === "passive" && value.enforcementGuaranteed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["enforcementGuaranteed"],
            message: "passive adapters cannot claim enforcement guarantees",
        });
    }
});
const PROFILE_VERSION = "1.0.0";
export const UNIVERSAL_RAIL_ADAPTER_PROFILES = {
    github: {
        kind: "github",
        mode: "fully_mediated",
        version: PROFILE_VERSION,
        supportedActions: ["none", "merge", "retry"],
        enforcementGuaranteed: true,
        takesCustody: false,
    },
    ci: {
        kind: "ci",
        mode: "fully_mediated",
        version: PROFILE_VERSION,
        supportedActions: ["none", "deploy", "retry"],
        enforcementGuaranteed: true,
        takesCustody: false,
    },
    jira: {
        kind: "jira",
        mode: "cooperative",
        version: PROFILE_VERSION,
        supportedActions: ["none", "mark_done", "retry"],
        enforcementGuaranteed: false,
        takesCustody: false,
    },
    linear: {
        kind: "linear",
        mode: "cooperative",
        version: PROFILE_VERSION,
        supportedActions: ["none", "mark_done", "retry"],
        enforcementGuaranteed: false,
        takesCustody: false,
    },
    erp: {
        kind: "erp",
        mode: "passive",
        version: PROFILE_VERSION,
        supportedActions: ["none", "write"],
        enforcementGuaranteed: false,
        takesCustody: false,
    },
    marketplace: {
        kind: "marketplace",
        mode: "cooperative",
        version: PROFILE_VERSION,
        supportedActions: ["none", "pay", "refund", "retry"],
        enforcementGuaranteed: false,
        takesCustody: false,
    },
    stripe: {
        kind: "stripe",
        mode: "cooperative",
        version: PROFILE_VERSION,
        supportedActions: ["none", "pay", "refund"],
        enforcementGuaranteed: false,
        takesCustody: false,
    },
    x402: {
        kind: "x402",
        mode: "fully_mediated",
        version: PROFILE_VERSION,
        supportedActions: ["none", "pay", "refund"],
        enforcementGuaranteed: true,
        takesCustody: false,
    },
    ap2: {
        kind: "ap2",
        mode: "cooperative",
        version: PROFILE_VERSION,
        supportedActions: ["none", "pay", "refund"],
        enforcementGuaranteed: false,
        takesCustody: false,
    },
};
for (const profile of Object.values(UNIVERSAL_RAIL_ADAPTER_PROFILES)) {
    railAdapterProfileSchema.parse(profile);
}
export const railVerificationSignalSchema = z
    .object({
    adapter: railAdapterProfileSchema,
    tenantId: protocolIdSchema,
    target: z.string().trim().min(1),
    canonicalVerdict: verificationStatusSchema,
    receiptId: protocolIdSchema,
    receiptPayloadDigest: sha256DigestSchema,
    receiptBytes: z.string().min(1),
    signature: machineReadableVerdictReceiptSchema.shape.signature,
    decision: enforcementDecisionSchema,
    intent: railActionIntentSchema,
    delivery: z.enum(["READY", "HELD", "REFUSED"]),
    reasonCodes: z.array(protocolIdSchema),
    takesCustody: z.literal(false),
})
    .strict();
export const railConnectorResultSchema = z
    .object({
    state: z.enum(["APPLIED", "FAILED"]),
    receiptPayloadDigest: sha256DigestSchema,
    externalRef: z.string().trim().min(1).nullable(),
    responseDigest: sha256DigestSchema.nullable(),
    reasonCodes: z.array(protocolIdSchema),
    completedAt: protocolTimestampSchema,
})
    .strict();
export const railWebhookRequestSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    method: z.literal("POST"),
    endpoint: z.string().url(),
    headers: z.record(z.string().min(1)),
    bodyBytes: z.string().min(1),
    bodyDigest: sha256DigestSchema,
    idempotencyKey: sha256DigestSchema,
})
    .strict();
export const railWebhookResponseSchema = z
    .object({
    status: z.number().int().min(100).max(599),
    bodyBytes: z.string(),
    externalRef: z.string().trim().min(1).nullable(),
    completedAt: protocolTimestampSchema,
})
    .strict();
export function createRailWebhookRequest(input) {
    const signal = railVerificationSignalSchema.parse(input.signal);
    const bodyBytes = canonicalize(signal);
    const bodyDigest = input.sha256(bodyBytes);
    return railWebhookRequestSchema.parse({
        protocolVersion: "excellent.universal-verification.v1",
        method: "POST",
        endpoint: input.endpoint,
        headers: {
            "content-type": "application/json",
            "x-excellent-protocol": "excellent.universal-verification.v1",
            "x-excellent-idempotency-key": signal.intent.idempotencyKey,
            "x-excellent-receipt-digest": signal.receiptPayloadDigest,
            "x-excellent-signing-key": signal.signature.keyId,
            "x-excellent-body-digest": bodyDigest,
        },
        bodyBytes,
        bodyDigest,
        idempotencyKey: signal.intent.idempotencyKey,
    });
}
export function createUniversalRailWebhookConnector(input) {
    const profile = railAdapterProfileSchema.parse(input.profile);
    return {
        profile,
        async apply(signal) {
            const request = createRailWebhookRequest({
                endpoint: input.endpoint,
                signal,
                sha256: input.sha256,
            });
            const response = railWebhookResponseSchema.parse(await input.transport.deliver(request));
            const responseBodyDigest = input.sha256(response.bodyBytes);
            return railConnectorResultSchema.parse({
                state: response.status >= 200 && response.status < 300 ? "APPLIED" : "FAILED",
                receiptPayloadDigest: signal.receiptPayloadDigest,
                externalRef: response.externalRef,
                responseDigest: input.sha256(canonicalize({
                    status: response.status,
                    bodyDigest: responseBodyDigest,
                })),
                reasonCodes: [
                    response.status >= 200 && response.status < 300
                        ? "webhook-accepted"
                        : `webhook-http-${response.status}`,
                ],
                completedAt: response.completedAt,
            });
        },
    };
}
function deliveryFor(input) {
    if (input.intent.kind === "none") {
        return {
            delivery: "HELD",
            reasonCodes: [...input.decision.reasonCodes, `disposition-${input.decision.disposition.toLowerCase()}`],
        };
    }
    if (!input.profile.supportedActions.includes(input.intent.kind)) {
        return {
            delivery: "REFUSED",
            reasonCodes: ["adapter-action-unsupported"],
        };
    }
    return { delivery: "READY", reasonCodes: [] };
}
export function createRailVerificationSignal(input) {
    const profile = railAdapterProfileSchema.parse(input.profile);
    const receipt = machineReadableVerdictReceiptSchema.parse(input.receipt);
    const decision = enforcementDecisionSchema.parse(input.decision);
    const intent = railActionIntentSchema.parse(input.intent);
    if (decision.receiptId !== receipt.payload.id ||
        decision.receiptPayloadDigest !== receipt.payloadDigest ||
        intent.receiptPayloadDigest !== receipt.payloadDigest ||
        intent.decisionId !== decision.id) {
        throw new Error("rail adapter binding mismatch");
    }
    const delivery = deliveryFor({ profile, decision, intent });
    return railVerificationSignalSchema.parse({
        adapter: profile,
        tenantId: receipt.payload.binding.tenantId,
        target: intent.target,
        // READ TIME, and the clock is the DECISION's own instant rather than a
        // fresh one: this signal is bound to `decision` by the digest check above,
        // so the verdict a connector is told must be the verdict that decision was
        // taken against. Reading a later clock here could hand a rail a canonical
        // verdict its disposition never saw.
        canonicalVerdict: resolveStoredVerdict({
            verdict: receipt.payload,
            now: decision.decidedAt,
        }).status,
        receiptId: receipt.payload.id,
        receiptPayloadDigest: receipt.payloadDigest,
        receiptBytes: canonicalize(receipt),
        signature: receipt.signature,
        decision,
        intent,
        ...delivery,
        takesCustody: false,
    });
}
export function createUniversalRailAdapter(input) {
    const profile = railAdapterProfileSchema.parse(input.connector.profile);
    return {
        profile,
        async execute(command) {
            const receipt = machineReadableVerdictReceiptSchema.parse(command.receipt);
            const trustRoot = await input.resolveTrustRoot(receipt.signature.keyId);
            if (!trustRoot)
                throw new Error("rail adapter rejected an untrusted receipt key");
            const receiptVerification = await verifyVerdictReceipt({
                receipt,
                sha256: input.sha256,
                trustRoot,
            });
            if (!receiptVerification.ok) {
                throw new Error(`rail adapter rejected receipt: ${receiptVerification.reason}`);
            }
            const policy = enforcementPolicySchema.parse(command.policy);
            const { decision, intent } = await input.service.decide({
                receipt,
                policy,
                actor: command.actor,
                target: command.target,
                override: command.override,
            });
            const signal = createRailVerificationSignal({
                profile,
                receipt,
                decision,
                intent,
            });
            if (signal.delivery !== "READY") {
                return { decision, intent, signal, effect: null };
            }
            const existingEffect = await input.service.getEffectByIdempotencyKey(intent.tenantId, intent.idempotencyKey);
            if (existingEffect?.state === "APPLIED" || existingEffect?.state === "PENDING") {
                return { decision, intent, signal, effect: existingEffect };
            }
            const attemptedAt = input.now();
            let connectorResult;
            try {
                connectorResult = railConnectorResultSchema.parse(await input.connector.apply(signal));
            }
            catch (error) {
                const detail = error instanceof Error ? error.message : String(error);
                connectorResult = railConnectorResultSchema.parse({
                    state: "FAILED",
                    receiptPayloadDigest: receipt.payloadDigest,
                    externalRef: null,
                    responseDigest: input.sha256(canonicalize({ error: detail })),
                    reasonCodes: ["connector-exception"],
                    completedAt: input.now(),
                });
            }
            if (connectorResult.receiptPayloadDigest !== receipt.payloadDigest) {
                throw new Error("rail connector response changed the canonical receipt digest");
            }
            const effect = createEnforcementEffect({
                id: input.id("effect"),
                intent,
                state: connectorResult.state,
                attemptNumber: (existingEffect?.attemptNumber ?? 0) + 1,
                sha256: input.sha256,
                connectorRef: `${profile.kind}@${profile.version}`,
                attemptedAt,
                completedAt: connectorResult.completedAt,
                externalRef: connectorResult.externalRef,
                responseDigest: connectorResult.responseDigest,
                reasonCodes: connectorResult.reasonCodes,
            });
            enforcementEffectSchema.parse(effect);
            await input.service.recordEffect({ decision, intent, effect, events: [] });
            await input.persistEffect?.({ decision, intent, effect });
            return { decision, intent, signal, effect };
        },
    };
}
